import {Component, Input, OnInit, Pipe, PipeTransform, Signal, signal} from '@angular/core';
import {catchError, debounceTime, distinctUntilChanged, filter, map, pluck, switchMap, tap} from 'rxjs/operators';
import {of} from 'rxjs';
import {errorText} from '../../graphql/error-link';
import {gqlLoad, LoadState} from '../../graphql/gql-load';
import {LoadRowComponent} from '../../graphql/load-row.component';
import {Apollo} from 'apollo-angular';
import gql from 'graphql-tag';
import {Folder, Organization, Query, SearchResult, SearchResults} from '../../types-repository';
import {FormControl, FormGroup, ReactiveFormsModule, FormBuilder} from '@angular/forms';
import {LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {FilterSectionComponent} from '../filter-section/filter-section.component';
import {RouterModule} from '@angular/router';

import {AssetIconComponent} from '../s3-browse/file-icon/asset-icon.component';
import {FileFolderLinkPipe} from '../file-folder-link.pipe';
import {RemovePipe} from '../../pipes/remove-pipe';
import {OrgResolverPipe} from '../../pipes/org-resolver-pipe';
import {GET_ROOTS} from '../../queries-repository';
import {ContextService} from '../../services/context.service';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {HttpHeaders} from '@angular/common/http';
import {DateComponent} from '../../date/date.component';


@Pipe({name: 'middleTruncate'})
export class MiddleTruncatePipe implements PipeTransform {
  transform(value: string, maxLength: number): string {
    if (!value || value.length <= maxLength) {
      return value;
    }
    const half = Math.floor((maxLength - 3) / 2);
    return value.slice(0, half) + '...' + value.slice(value.length - half);
  }
}

@Component({
  selector: 'app-search-page',
  templateUrl: 'search-page.component.html',
  styleUrls: ['search-page.component.scss'],
  imports: [
    LogoAreaComponent,
    HeadlineSectionComponent,
    FilterSectionComponent,
    ReactiveFormsModule,
    RouterModule,
    AssetIconComponent,
    FileFolderLinkPipe,
    RemovePipe,
    MiddleTruncatePipe,
    OrgResolverPipe,
    DateComponent,
    LoadRowComponent,
]
})
export class SearchPageComponent implements OnInit {
  organization: Signal<Organization>;
  items: Array<string>;
  search_ongoing = false;
  /** The last search: loading, its results or why it failed. */
  searchState: LoadState<SearchResults> = {loading: false, data: null, error: null};
  /** Why the storage roots (the root filter) could not be loaded. */
  rootsError = signal<string | null>(null);
  searchResults: SearchResults = {
    items: [],
    total: 0
  };


  filters: FormGroup<{
    t: FormControl<string>;
    p: FormControl<string>;
    r: FormControl<string | null>;
    d1: FormControl<string>;
    d2: FormControl<string>;
    sd: FormControl<string>;
  }>;
  storageRoots: Signal<Array<Folder>>;

  searchHits = (term: string, parent: string, prefix: string, d1, d2, sd) => this.apollo.query<Query>({
    query: gql`
      query SearchMetadata($searchTerm: String!, $parent: ID, $prefix: String, $not_before: AWSDateTime, $not_after: AWSDateTime, $show_deleted: Boolean) {
        search(input: { search_string: $searchTerm, parent_id: $parent, prefix: $prefix, not_before: $not_before, not_after: $not_after, show_deleted: $show_deleted } ) {
          items {
            item {
              ... on File {
                id
                name
                size
                deleted
                archived
                last_modified
              }
              ... on Folder {
                id
                name
                last_modified
              }
            }
            score
          }
          total
        }
      }
    `,
    variables: {searchTerm: term, parent: parent, prefix: prefix, not_before: d1, not_after: d2, show_deleted: sd}

  })

  ngOnInit() {
    this.filters.valueChanges.pipe(
      debounceTime(300),

      // 1) Destructure and return a tuple of [t, r]
      map(({t, p, r, d1, d2, sd}) => {
        return [t, p, r, d1, d2, sd] as [string, string, string, string, string, string];
      }),
      // 2) Make sure distinctUntilChanged compares the tuple, not the outer object
      // gqlLoad catches a failed search inside switchMap: the error row shows, and the next change
      // of the filters searches again.
      switchMap(([term, parent, prefix, d1, d2, sd]) => gqlLoad(
        this.searchHits(
          term,
          parent,
          prefix,
          d1 !== '' ? new Date(Date.parse(d1)).toISOString() : null,
          d2 !== '' ? new Date(Date.parse(d2)).toISOString() : null,
          sd === 'true'),
        res => res.data?.search ?? {items: [], total: 0}))
    )
      .subscribe(state => {
        this.searchState = state;
        this.search_ongoing = state.loading;
        if (!state.loading) {
          this.searchResults = state.data ?? {items: [], total: 0};
        }
      });
  }

  constructor(
    private apollo: Apollo,
    private context: ContextService,
    formBuilder: FormBuilder) {
    this.filters = formBuilder.group({
        t: '',
        p: '',
        r: '',
        d1: '',
        d2: '',
        sd: '',
      }
    )
    this.organization = this.context.organization
    console.log(this.filters);

    // Follows the organization: an org switch on this page reuses the component, and a one-off query
    // from the constructor kept the previous organization's roots (VTK-3591). organizationId() is also
    // what the cache keys `roots` on, so key and header agree.
    this.storageRoots = toSignal(toObservable(context.organizationId).pipe(
      filter((id): id is string => !!id),
      distinctUntilChanged(),
      tap(() => this.rootsError.set(null)),
      switchMap(id => this.apollo.query<Query>({
        query: GET_ROOTS(id),
        context: {headers: new HttpHeaders({'X-Castlabs-Organization': id})},
      }).pipe(
        filter(res => !!res.data?.roots),
        map(res => res.data.roots),
        // Inside the switchMap: a failed load says why, and the next organization loads again.
        catchError(error => {
          this.rootsError.set(errorText(error));
          return of([] as Folder[]);
        }),
      )),
    ), {initialValue: []})
  }
}
